/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SyncLegalRequirementsDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CustomSelectV2 from "../common/select/CustomSelectV2";
import CustomButtonV2 from "../common/button/CustomButtonV2";
import { HandleApiActions } from "../../../redux/actions";

function SyncLegalRequirementsDialog({
  open,
  onClose,
  selectedStandard,
  selectedChapter,
  onSyncSuccess,
}) {
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [trackerOptions, setTrackerOptions] = useState([]);
  const [selectedTracker, setSelectedTracker] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedProject("");
      setSelectedTracker("");
      setProjectOptions([]);
      setTrackerOptions([]);
      setErrorMessage("");
    }
  }, [open]);

  const handleClose = () => {
    if (onClose) onClose(false);
  };

  const isSubmitDisabled =
    loading ||
    !selectedProject ||
    !selectedTracker ||
    !selectedStandard?.ls_id ||
    !selectedChapter;

  const moduleType = "LEGAL REQUIREMENTS";

  const resolveTrackerId = (tracker) => {
    const directId = Number(tracker?.id);
    if (Number.isFinite(directId)) return directId;

    const uri = tracker?.uri || "";
    const match = uri.match(/\/tracker\/(\d+)/i);
    if (match?.[1]) {
      const parsed = Number(match[1]);
      if (Number.isFinite(parsed)) return parsed;
    }

    return null;
  };

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await dispatch(
        HandleApiActions({
          api_name: "/ICM/connectors/invoke",
          https_method: "POST",
          params: {
            type: "codebeamer",
            action: "getProjects",
            module_type: moduleType,
          },
        }),
      );

      if (!resp?.error && Array.isArray(resp?.data)) {
        const options = resp.data.map((project) => ({
          label: project.name,
          value: Number(project.id),
        }));
        setProjectOptions(options);
      } else {
        setErrorMessage(resp?.msg || "Failed to fetch projects");
      }
    } catch (error) {
      setErrorMessage("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const fetchTrackers = useCallback(
    async (projectId) => {
      if (!projectId) return;
      const resolvedProjectId = Number(projectId);

      setLoading(true);
      try {
        const resp = await dispatch(
          HandleApiActions({
            api_name: "/ICM/connectors/invoke",
            https_method: "POST",
            params: {
              type: "codebeamer",
              action: "getTrackers",
              module_type: moduleType,
              payload: { projectId: resolvedProjectId },
            },
          }),
        );

        if (!resp?.error && Array.isArray(resp?.data)) {
          const requirementTrackers = resp.data.filter(
            (tracker) => tracker?.type?.name === "Requirement",
          );

          const options = requirementTrackers
            .map((tracker) => {
              const trackerId = resolveTrackerId(tracker);
              return trackerId
                ? { label: tracker.name, value: trackerId }
                : null;
            })
            .filter(Boolean);
          setTrackerOptions(options);
        } else {
          setErrorMessage(resp?.msg || "Failed to fetch trackers");
        }
      } catch (error) {
        setErrorMessage("Failed to fetch trackers");
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;
    setLoading(true);
    try {
      const resp = await dispatch(
        HandleApiActions({
          api_name: "/ICM/connectors/invoke",
          https_method: "POST",
          params: {
            type: "codebeamer",
            action: "syncLegalRequirements",
            module_type: moduleType,
            payload: {
              trackerId: Number(selectedTracker),
              ls_id: selectedStandard?.ls_id,
              chapter: selectedChapter,
            },
          },
          show_toast: true,
        }),
      );

      if (!resp?.error) {
        if (onSyncSuccess) {
          onSyncSuccess(resp);
        }
        handleClose();
      } else {
        setErrorMessage(resp?.msg || "Sync failed");
      }
    } catch (error) {
      setErrorMessage("Sync failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchProjects();
    }
  }, [open, fetchProjects]);

  useEffect(() => {
    if (!open || !selectedProject) return;
    fetchTrackers(selectedProject);
  }, [open, selectedProject, fetchTrackers]);


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Sync CodeBeamer</Typography>
          <IconButton aria-label="close" onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <CustomSelectV2
            label="Project Name"
            required={true}
            optionsList={projectOptions}
            selectedValue={selectedProject}
            handleOnChange={(e) => {
              const value = e.target.value;
              setSelectedProject(value === "" ? "" : Number(value));
              setSelectedTracker("");
              setTrackerOptions([]);
            }}
          />
          <CustomSelectV2
            label="Requirement Tracker"
            required={true}
            optionsList={trackerOptions}
            selectedValue={selectedTracker}
            handleOnChange={(e) => {
              const value = e.target.value;
              setSelectedTracker(value === "" ? "" : Number(value));
            }}
          />

          {errorMessage && (
            <Typography sx={{ color: "#B91C1C" }}>{errorMessage}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CustomButtonV2
          type="cancel"
          btnName="Cancel"
          handleOnClick={handleClose}
        />
        <CustomButtonV2
          type="submit"
          btnName="Add"
          handleOnClick={handleSubmit}
          disabled={isSubmitDisabled}
        />
      </DialogActions>
    </Dialog>
  );
}

export default SyncLegalRequirementsDialog;
